<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getProductRoute } from '@shopware/helpers'

interface SidenavClickAndReserveProps {
    showCloseButton?: boolean
    maxHeight?: string
    emptyMessage?: string
}

const props = withDefaults(defineProps<SidenavClickAndReserveProps>(), {
    showCloseButton: true,
    maxHeight: 'calc(100vh - 200px)',
    emptyMessage: ''
})

// Global sidenav state management
const { isOpen } = useSidenav()
// Ensure 'clickAndReserve' is a valid SidenavType or cast it if necessary
const clickAndReserveOpen = isOpen('clickAndReserve')

// i18n
const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

// Initialize data and state
const { clickAndReserveProducts, clickAndReserveItems, count, loadClickAndReserveProducts } = useClickAndReserve()
const isLoading = ref(false)

// Navigate to click and reserve page and close sidenav
const handleViewClickAndReserve = () => {
    navigateTo(formatLink('/abholung'))
    clickAndReserveOpen.value = false
}

// Navigate to product detail page and close sidenav
const handleProductClick = (product: Schemas['Product']) => {
    navigateTo(formatLink(getProductRoute(product)))
    clickAndReserveOpen.value = false
}

// Watch for open/close to fetch data
watch(clickAndReserveOpen, (newValue) => {
    if (newValue) {
        loadClickAndReserveProducts()
    }
})

// Watch for changes in click and reserve items and refresh product list
watch(clickAndReserveItems, () => {
    if (clickAndReserveOpen.value) {
        loadClickAndReserveProducts()
    }
})

// Automatically fetch on component mount if opened
onMounted(() => {
    if (clickAndReserveOpen.value) {
        loadClickAndReserveProducts()
    }
})
</script>

<template>
    <SidenavOverlay
        v-model="clickAndReserveOpen"
        direction="right"
        width-class="w-full md:w-[600px]"
    >
        <div class="flex flex-col h-full bg-base-100">
            <!-- Header -->
            <SidenavHeader
                :title="$t('clickAndReserve.title')"
                :show-close-button="showCloseButton"
                :close-aria-label="$t('clickAndReserve.sidenav.close')"
                @close="clickAndReserveOpen = false"
            />

            <!-- Loading state -->
            <div v-if="isLoading" class="flex items-center justify-center flex-grow p-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-border" />
            </div>

            <!-- Empty click and reserve state -->
            <div
                v-else-if="!count || count === 0"
                class="flex flex-col items-center justify-center flex-grow p-8 bg-base-200"
            >
                <FoundationIcon name="map-pin" class="w-14 h-14 mb-4 text-base-500" />
                <p class="text-2xl font-bold mb-4">{{ props.emptyMessage || $t('clickAndReserve.emptyLabel') }}</p>

                <FoundationButton
                    color="primary"
                    @click="clickAndReserveOpen = false"
                >
                    {{ $t('clickAndReserve.discover') }}
                </FoundationButton>
            </div>

            <!-- Click and reserve items -->
            <div v-else class="overflow-auto" :style="{ 'max-height': props.maxHeight }">
                <div class="border-b border-border px-6 md:px-9 py-3 text-sm font-bold">
                    {{ count }} {{ $t('clickAndReserve.products') }}
                </div>

                <div>
                    <ProductListItem
                        v-for="product in clickAndReserveProducts"
                        :key="product.id"
                        :product="product"
                        variant="clickAndReserve"
                        class="px-6 md:px-9 py-6 border-b border-border last:border-b-0"
                        @product-click="handleProductClick"
                    />
                </div>
            </div>

            <!-- Footer with action button -->
            <div v-if="count && count > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 md:px-9 py-4 border-t border-border">
                <div>
                    <FoundationButton
                        color="secondary"
                        class="w-full"
                        @click="clickAndReserveOpen = false"
                    >
                        {{ $t('clickAndReserve.sidenav.continueShopping') }}
                    </FoundationButton>
                </div>

                <div>
                    <FoundationButton
                        color="primary"
                        class="w-full"
                        @click="handleViewClickAndReserve"
                    >
                        {{ $t('clickAndReserve.sidenav.viewClickAndReserve') }}
                    </FoundationButton>
                </div>
            </div>
        </div>
    </SidenavOverlay>
</template>
